import { Switch } from "@/components/ui/switch";
import { EditableCell } from "@/components/admin/EditableCell";
import { fmtBT, fmtNok, fmtRate } from "@/lib/format";

const TYPES = [["general", "Generelle fartøy", "general_enabled"], ["cruise", "Cruiseskip", "cruise_enabled"], ["fishing", "Fiskefartøy", "fishing_enabled"], ["leisure", "Fritidsfartøy", "leisure_enabled"]];

const Field = ({ label, children }) => (
  <div className="flex items-center gap-1.5 text-xs text-slate-500">{label}<div className="w-28">{children}</div></div>
);

const FeeLine = ({ title, hint, fee, onChange, testId, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-2.5 border-t border-slate-100 first:border-t-0">
    <label className="flex items-center gap-3 cursor-pointer min-w-[200px]">
      <Switch checked={!!fee.enabled} onCheckedChange={(v) => onChange({ ...fee, enabled: v })} data-testid={`${testId}-switch`} />
      <span><span className="block text-sm font-medium text-slate-900">{title}</span><span className="block text-xs text-slate-500">{hint}</span></span>
    </label>
    {fee.enabled && <div className="flex flex-wrap items-center gap-3">{children}</div>}
  </div>
);

const METHODS = [["bt", "Brutto tonn"], ["percent", "% av kaivederlag"]];

const MethodPicker = ({ value, onChange, testId }) => (
  <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50" role="radiogroup" aria-label="Beregningsmetode ISPS">
    {METHODS.map(([k, l]) => (
      <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onChange(k)} data-testid={`${testId}-method-${k}`}
        className={`px-2.5 py-1 text-xs rounded transition-colors ${value === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-white"}`}>{l}</button>
    ))}
  </div>
);

export const TypeBlock = ({ t, label, ex, set, unit = "BT", single = false }) => {
  const patch = (k, v) => set({ ...ex, [k]: v });
  const num = (k, f, field) => <EditableCell value={ex[k][field]} format={f} onCommit={(v) => patch(k, { ...ex[k], [field]: v })} testId={`extras-${t}-${k}-${field}`} className="font-medium text-slate-900" />;
  return (
    <div className="rounded-md border border-slate-200 bg-white px-4 py-2" data-testid={`extras-${t}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 pt-1.5 pb-1">{label}</div>
      {ex.isps && (
        <FeeLine title="ISPS" hint={ex.isps.method === "percent" ? `Prosent av ${single ? "tabellens beløp" : "kaivederlaget"}` : `${unit} × sats × døgn`} fee={ex.isps} onChange={(v) => patch("isps", v)} testId={`extras-${t}-isps`}>
          <MethodPicker value={ex.isps.method || "bt"} onChange={(m) => patch("isps", { ...ex.isps, method: m })} testId={`extras-${t}-isps`} />
          {ex.isps.method === "percent"
            ? <Field label="Prosent %">{num("isps", fmtBT, "percent")}</Field>
            : <Field label={`Kr per ${unit}`}>{num("isps", fmtRate, "rate")}</Field>}
        </FeeLine>
      )}
      <FeeLine title="Avfallsgebyr" hint={ex.isps ? `${unit} × sats, med minstepris` : "Fast beløp per anløp"} fee={ex.waste} onChange={(v) => patch("waste", v)} testId={`extras-${t}-waste`}>
        {ex.isps ? (<>
          <Field label={`Kr per ${unit}`}>{num("waste", fmtRate, "rate")}</Field>
          <Field label="Minstepris">{num("waste", fmtNok, "min")}</Field>
        </>) : <Field label="Beløp kr">{num("waste", fmtNok, "amount")}</Field>}
      </FeeLine>
    </div>
  );
};

export const ExtrasEditor = ({ c, save }) => {
  const shown = TYPES.filter(([t, , flag]) => (t === "general" ? c[flag] !== false : c[flag]));
  return (
    <div className="space-y-3" data-testid="extras-editor">
      <div>
        <h3 className="font-display text-lg font-semibold text-slate-900">ISPS og avfallsgebyr</h3>
        <p className="text-xs text-slate-500">Slå på for hver fartøytype. Gebyrene vises som egne linjer i kalkulatoren og tas med i summen. Er de av, vises de ikke.</p>
      </div>
      <div className="grid gap-3">
        {shown.map(([t, label]) => <TypeBlock key={t} t={t} label={label} ex={c.extras[t]} set={(v) => save("extras", { ...c.extras, [t]: v })} />)}
        {(c.custom_calcs || []).map((x, i) => (
          <TypeBlock key={x.id} t={`custom-${x.id}`} label={x.table.name} ex={x.extras} unit={x.basis === "length" ? "meter" : "BT"} single
            set={(v) => save("custom_calcs", c.custom_calcs.map((y, j) => (j === i ? { ...y, extras: v } : y)))} />
        ))}
      </div>
    </div>
  );
};
